import { useTranslation } from "@i18n";
import SelectDropDown, { type SelectEventArgs } from "@CubeComponents/Mui_Form/SelectDropDown";
import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { showToastNotification } from "@Reducers/toastSlice";
import { TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS } from "@Services/constants";
import {
  clearCostDatabaseRedux,
  discardCostDatabaseDraftVersion,
  getAllVersionsOfCostDatabase,
  getCostDatabase,
  getCostDatabaseById,
  itemListSources,
  publishCostDatabaseVersion,
} from "@Actions/estimation";
import { EstimateCostDatabaseItemsGridNew } from "@Features/estimation";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { useNavigate, useParams } from "react-router-dom";
import { getPortalSettingsTabUrl } from "@Services/utils/helpers";
import { setBreadCrumbData } from "@Reducers/breadcrumbsdata";
import type { RootState } from "src/reducers";
import IconComponent from "@CubeComponents/icons";
import { getCostDatabasesUrl } from "@Features/projects";
import Text from "@CubeComponents/html/Text/Text";
import type React from "react";
import { objectIdString, type ObjectIdString } from "@Helpers/schemas/common.schema";
import type { EstimateCurrency } from "@ServerTypes/estimates/estimates.schema";
import type { GetCostDatabaseDataResponse } from "@ServerTypes/costDatabase/costDatabase.schema";

/**
 * One cost database as `/costDatabase/getCostDatabaseData` returns it — the single shape this page
 * holds, whether it arrived through the redux slice or through `getCostDatabaseById`.
 *
 * `currency.selectedSymbol` is added on top: it IS on the document (`currencySchema` in
 * costDatabase.collection.ts) and IS returned by the endpoint's `currency: 1` projection (446/446
 * staging CostDatabases carry it), but `getCostDatabaseDataResponse` only declares `currency.id`
 * and `currency.name`. TODO: add it there and drop this intersection.
 */
type CostDatabaseRecord = NonNullable<GetCostDatabaseDataResponse["result"]>[number] & {
  currency?: { selectedSymbol?: { name?: string } };
};

type CostDatabaseProps = {
  costDatabaseId: ObjectIdString;
  showOnlyGrid?: boolean;
  /**
   * Forwarded verbatim to the items grid's `selectionEvent` — this page never calls it itself.
   * Derived from the grid so the two cannot drift: the grid emits a plain array of item rows, not
   * the `{ selectedItems }` wrapper this prop used to also declare.
   */
  costDatabaseItemsSelection?: React.ComponentProps<typeof EstimateCostDatabaseItemsGridNew>["selectionEvent"];
  publishedVersion?: boolean;
};

type CostDatabaseVersionMenuItem = {
  costDBversionId: string;
  text: string;
};

function CostDatabase(props: CostDatabaseProps) {
  const { t } = useTranslation();
  const { costDatabaseId, showOnlyGrid = false, costDatabaseItemsSelection, publishedVersion } = props;
  // Route param is the external entry point for this id; the backend route schema validates it.
  const params = useParams<{ costDatabaseVersionId: ObjectIdString }>();

  const costDatabaseVersionId = params.costDatabaseVersionId;
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const costDatabase = useSelector((state: RootState) => state.costDatabase);

  const [allCostDatabaseVersions, setAllCostDatabaseVersions] = useState<CostDatabaseVersionMenuItem[]>([]);
  const [updateChildKey, setUpdateChildKey] = useState(true);
  const [currentOpenCostDatabase, setCurrentOpenCostDatabase] = useState<CostDatabaseRecord>({});

  const portalSettingInfo = useSelector((state: RootState) => state.portalSettingInfo);
  const canEditCostDatabase = !!portalSettingInfo.costDatabase?.editCostDatabases?.enable;
  // `_id` is `unknown` on getCostDatabaseDataResponse (it is the document's ObjectId, serialized to
  // a string); only its presence is read here, so keep it out of JSX as a plain boolean.
  const isCostDatabaseLoaded = Boolean(currentOpenCostDatabase._id);
  // `currency.id` is an ObjectId in costDatabase.collection.ts and reaches the client as a 24-hex
  // string, but `getCostDatabaseDataResponse` declares it `unknown` — so parse it rather than
  // assert. `undefined` until the cost database has loaded, and the grid render below is gated on
  // it alongside the `_id` gate it already had, instead of minting a branded "" sentinel for the
  // required `EstimateCurrency.id`.
  const currencyId = objectIdString.safeParse(currentOpenCostDatabase.currency?.id).data;
  const safeCurrencyData: EstimateCurrency | undefined = currencyId
    ? {
        id: currencyId,
        selectedSymbol: { name: currentOpenCostDatabase.currency?.selectedSymbol?.name ?? "" },
      }
    : undefined;

  useEffect(() => {
    if (costDatabase._id && !showOnlyGrid) {
      setCurrentOpenCostDatabase({ ...costDatabase });
      dispatch(
        setBreadCrumbData([
          { text: t("Portal Settings") },
          { text: t("Cost Databases"), link: getPortalSettingsTabUrl("costDatabases") },
          { text: costDatabase.name },
        ]),
      );
    }
  }, [costDatabase, showOnlyGrid]);

  const fetchCostDatabase = useCallback(() => {
    dispatch(getCostDatabase({ _id: costDatabaseId, costDatabaseVersionId }));
  }, [costDatabaseId, costDatabaseVersionId]);

  const fetchVersionsOfCostDatabase = useCallback(async () => {
    const response = await getAllVersionsOfCostDatabase({ value: { costDatabaseId } });
    if (response.data.status && Array.isArray(response.data.result)) {
      // `costDBversionId` is the version's ObjectId serialized to a string, but the response schema
      // declares it `unknown`; a row without one cannot address a version, so it is dropped.
      const items = response.data.result.flatMap(({ versionNumber, costDBversionId }) =>
        typeof costDBversionId === "string" ? [{ costDBversionId, text: String(versionNumber) }] : [],
      );

      setAllCostDatabaseVersions(items);
    }
  }, [costDatabaseId]);

  async function publishVersion() {
    const response = await publishCostDatabaseVersion({ costDatabaseId });
    if (response.data.status) {
      dispatch(
        showToastNotification({
          content: t("The cost database has been published"),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
      fetchCostDatabase();
      fetchVersionsOfCostDatabase();
      return;
    }

    dispatch(
      showToastNotification({
        content: t("The cost database could not be published"),
        type: TOAST_TYPE_ERROR,
      }),
    );
  }

  async function discardVersion() {
    const response = await discardCostDatabaseDraftVersion({ costDatabaseId });
    if (response.data.status) {
      dispatch(
        showToastNotification({
          content: t("The cost database draft has been discarded"),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
      fetchCostDatabase();
      fetchVersionsOfCostDatabase();
      setUpdateChildKey((prev) => !prev);
      return;
    }

    dispatch(
      showToastNotification({
        content: t("The cost database draft could not be discarded"),
        type: TOAST_TYPE_ERROR,
      }),
    );
  }

  function selectActionFromMenu(args: SelectEventArgs) {
    // `properties` is the CostDatabaseVersionMenuItem this component put into `items`, but
    // SelectDropDown re-types it through an index signature on the way back out.
    const { costDBversionId } = args.item.properties;
    if (typeof costDBversionId !== "string") return;

    window.open(`${window.location.href}/costDatabaseVersion/${costDBversionId}`, "_blank");
  }

  const versionsList = useCallback(
    () => (
      <SelectDropDown
        className="me-2.5"
        items={allCostDatabaseVersions}
        select={selectActionFromMenu}
        data-testid="costdatbaseVersionList"
        maxHeight="15.625rem"
      >
        <ButtonComponent buttonType={BUTTON_TYPES.GRADIENT_BUTTON}>
          {currentOpenCostDatabase.versionNumber ? `v${currentOpenCostDatabase.versionNumber}` : "Draft"}
        </ButtonComponent>
      </SelectDropDown>
    ),
    [currentOpenCostDatabase.versionNumber, allCostDatabaseVersions],
  );

  const unmountCostDatabase = useCallback(() => {
    dispatch(clearCostDatabaseRedux());
  }, []);

  const initForCurrentCostDatabase = useCallback(async () => {
    const response = await getCostDatabaseById({ _id: costDatabaseId });
    const record = response.data.status ? response.data.result?.[0] : undefined;
    if (record) {
      setCurrentOpenCostDatabase(record);
    }
  }, [costDatabaseId]);

  useEffect(() => {
    if (!showOnlyGrid) {
      fetchCostDatabase();
      fetchVersionsOfCostDatabase();
      return unmountCostDatabase;
    }
    initForCurrentCostDatabase();
  }, [fetchCostDatabase, fetchVersionsOfCostDatabase, initForCurrentCostDatabase, showOnlyGrid, unmountCostDatabase]);

  const handleBackClick = () => {
    navigate(getCostDatabasesUrl());
  };

  return (
    <div className="flex w-full flex-col">
      {!showOnlyGrid && (
        <div className="flex items-center justify-between py-2 gap-4">
          <div className="flex items-center gap-4 w-full overflow-auto">
            <IconComponent
              className="cursor-pointer"
              name="BackwardArrow"
              onClick={handleBackClick}
              width="1.5rem"
              height="1.5rem"
            />
            <div className="flex w-full flex-col overflow-auto">
              <div className="flex items-center justify-start w-full" data-testid="costDatabaseNameAndVerison">
                <Text className="text-2xl font-semibold" data-testid="costDatabaseName">
                  {currentOpenCostDatabase.name}
                </Text>
                {/* <p
                  className="ms-5 rounded border border-[#192048] px-1 py-0.5 text-xs"
                  data-testid="costDatabaseVersion"
                >
                  {currentOpenCostDatabase.versionNumber ? `v${currentOpenCostDatabase.versionNumber}` : "Draft"}
                </p> */}
              </div>
              <Text className="text-sm" data-testid="costDatabaseDesc">
                {currentOpenCostDatabase.description}
              </Text>
            </div>
          </div>

          <div data-testid="costdatbaseVersionBtn" className="flex gap-4 items-center">
            {costDatabaseVersionId ? (
              <p data-testid="draftOrCostdatbaseVersion">
                {currentOpenCostDatabase.versionNumber ? `v${currentOpenCostDatabase.versionNumber}` : "Draft"}
              </p>
            ) : (
              versionsList()
            )}
            {!costDatabaseVersionId && (
              <ButtonComponent
                data-testid="publishCostdatbase"
                className="me-2.5"
                disabled={!canEditCostDatabase || Boolean(currentOpenCostDatabase.versionNumber)}
                onClick={publishVersion}
                buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
              >
                {currentOpenCostDatabase.versionNumber ? t("Published") : t("Publish")}
              </ButtonComponent>
            )}
            {!currentOpenCostDatabase.versionNumber && !costDatabaseVersionId && (
              <ButtonComponent
                data-testid="discardCostdatbase"
                className="me-2.5"
                disabled={!canEditCostDatabase}
                onClick={discardVersion}
                buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
              >
                {t("Discard")}
              </ButtonComponent>
            )}
          </div>
        </div>
      )}
      {isCostDatabaseLoaded && safeCurrencyData && (
        <EstimateCostDatabaseItemsGridNew
          costDatabaseId={costDatabaseId}
          costDatabaseVersionId={costDatabaseVersionId}
          showOnlyGrid={showOnlyGrid}
          isStaticData={true}
          selectionEvent={costDatabaseItemsSelection}
          key={String(updateChildKey)}
          source={itemListSources.COST_DATABASE}
          fetchCostDatabase={fetchCostDatabase}
          fetchVersionsOfCostDatabase={fetchVersionsOfCostDatabase}
          currencyData={safeCurrencyData}
          publishedVersion={publishedVersion ?? undefined}
          canEdit={canEditCostDatabase}
        />
      )}
    </div>
  );
}

export default CostDatabase;
